import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PiDownloadSimpleLight, PiTrashLight, PiPaperclipLight } from 'react-icons/pi';
import Badge from '../TailwindComponents/Badge';
import CheckBox from '../TailwindComponents/CheckBox';
import Skeleton from '../TailwindComponents/Skeleton';
import { formatRelativeTime, formatAbsoluteTime } from '../feed/feedTime';
import FileIcon, { fileTypeLabel } from './fileIcon';
import { Attachment, formatFileSize } from './attachmentModel';

/**
 * THE attachment list.
 *
 * The same five files rendered four different ways: a resizable `Table` with
 * an absolute `MMM DD, YYYY HH:mm` stamp on the task tab, a bare row of
 * paperclip links in the matter task panel, another `Table` on contacts with
 * a different column order, and a single hard-coded card on review. Dates
 * were English-only in two of them.
 *
 * One row shape, one date rule (relative label, absolute on `title`, through
 * `feedTime` like every other chronology in the app), one empty state.
 */

export interface AttachmentListProps {
  attachments: Attachment[];
  loading?: boolean;
  /**
   * `canvas` - the list IS the surface (a tab body): roomier rows.
   * `pane` - nested inside a card or surface pane: tighter, quieter rows.
   */
  density?: 'canvas' | 'pane';
  /**
   * `stacked` (default) - name over a `size · uploader · when` metadata line.
   *   Right in a narrow pane, where there is no room for columns.
   * `columns` - name, uploaded by, date-time, size and the action cluster as
   *   ALIGNED COLUMNS on one line, with a header strip naming them.
   *
   * On a full-width tab the stacked shape reads as one file spilling over two
   * rows, which is exactly what it looks like: two rows. Columns collapse back
   * to the stacked shape below `lg`, so a narrowed window is not a wall of
   * clipped cells. Ignored at `density="pane"` - a pane is never wide enough.
   */
  layout?: 'stacked' | 'columns';
  /** Row press. Without it the name is plain text rather than a control. */
  onOpen?: (attachment: Attachment) => void;
  onDownload?: (attachment: Attachment) => void;
  /**
   * Extra per-row controls, rendered between Download and Remove.
   *
   * For actions only one host has - a playbook source document can be swapped
   * for a newer revision, an ordinary attachment cannot - so the row keeps one
   * shape instead of growing a prop per caller. Return `null` to give a
   * particular row nothing.
   */
  rowActions?: (attachment: Attachment) => React.ReactNode;
  /** Omit to hide the remove control entirely. */
  onRemove?: (attachment: Attachment) => void;
  /** Gate removal per row (or wholesale). Default: allowed when `onRemove` is set. */
  canRemove?: boolean | ((attachment: Attachment) => boolean);
  /** Disables the row controls while a mutation is in flight. */
  busy?: boolean;
  /**
   * Opt-in multi-select. Pass both to get a checkbox per row; the host owns
   * the ids and whatever bulk action it hangs off them.
   */
  selectedIds?: string[];
  onToggleSelect?: (attachment: Attachment) => void;
  emptyTitle?: string;
  emptyHint?: string;
  emptyIcon?: React.ReactNode;
  /** Rows skeleton count while `loading`. Default 4. */
  skeletonRows?: number;
  className?: string;
  'data-tour-id'?: string;
}

const AttachmentList: React.FC<AttachmentListProps> = ({
  attachments,
  loading = false,
  density = 'canvas',
  layout = 'stacked',
  onOpen,
  onDownload,
  rowActions,
  onRemove,
  canRemove,
  busy = false,
  selectedIds,
  onToggleSelect,
  emptyTitle,
  emptyHint,
  emptyIcon,
  skeletonRows = 4,
  className,
  'data-tour-id': dataTourId,
}) => {
  const { t } = useTranslation();
  const compact = density === 'pane';
  // A pane is never wide enough for columns, whatever the caller asked for.
  const columns = layout === 'columns' && !compact;

  if (loading) {
    return (
      <div className={clsx('flex-1 min-h-0 overflow-y-auto', className)} data-tour-id={dataTourId}>
        <Skeleton variant="list" rows={skeletonRows} />
      </div>
    );
  }

  if (!attachments.length) {
    return (
      <div
        className={clsx('flex-1 min-h-0 flex items-center justify-center', compact ? 'py-6' : 'py-12', className)}
        data-tour-id={dataTourId}
      >
        <div className="text-center px-4">
          <div
            className={clsx(
              'mx-auto rounded-full bg-surface-sunken flex items-center justify-center',
              compact ? 'h-9 w-9 mb-2' : 'h-12 w-12 mb-3'
            )}
          >
            {emptyIcon || <PiPaperclipLight className={compact ? 'h-4 w-4 text-fg-muted' : 'h-6 w-6 text-fg-muted'} />}
          </div>
          <p className={clsx('text-fg-strong', compact ? 'text-xs' : 'text-sm font-medium')}>
            {emptyTitle || t('No attachments')}
          </p>
          {!compact && (
            <p className="text-xs text-fg-muted mt-1">
              {emptyHint || t('Files added here appear for everyone with access.')}
            </p>
          )}
        </div>
      </div>
    );
  }

  const removable = (attachment: Attachment) => {
    if (!onRemove) return false;
    if (typeof canRemove === 'function') return canRemove(attachment);
    return canRemove !== false;
  };

  /* Column geometry, declared once so the header strip and every row agree.
     Below `lg` the columns are hidden and the stacked metadata line takes
     over, so these widths never have to survive a narrow window. */
  const COL_UPLOADER = 'hidden lg:block w-40 flex-shrink-0 truncate text-xs text-fg-muted';
  const COL_WHEN = 'hidden lg:block w-40 flex-shrink-0 truncate text-xs text-fg-muted';
  const COL_SIZE = 'hidden lg:block w-20 flex-shrink-0 text-end text-xs text-fg-muted tabular-nums';

  const rows = (
    <ul
      className={clsx(
        'flex-1 min-h-0 overflow-y-auto custom-scrollbar-main',
        compact ? 'space-y-1.5' : 'space-y-2',
        // The header owns the outer class in columns mode.
        !columns && className
      )}
      data-tour-id={columns ? undefined : dataTourId}
    >
      {attachments.map((attachment) => {
        const size = formatFileSize(attachment.size);
        const when = formatRelativeTime(attachment.uploadedAt, t);
        const whenTitle = formatAbsoluteTime(attachment.uploadedAt);
        const name = attachment.name || t('Untitled file');
        const selected = selectedIds?.includes(attachment.id) ?? false;

        return (
          <li
            key={attachment.id}
            className={clsx(
              'flex items-center gap-3 rounded-md border bg-surface-sunken/70',
              'transition-colors duration-200 ease-slide hover:border-champagne/40',
              selected ? 'border-champagne/60' : 'border-hairline-soft',
              compact ? 'px-2.5 py-2' : 'px-3 py-2.5'
            )}
          >
            {onToggleSelect && (
              <CheckBox
                label={t('Select {{name}}', { name })}
                hideLabel
                translate={false}
                checked={selected}
                onChange={() => onToggleSelect(attachment)}
                className="flex-shrink-0"
              />
            )}
            <FileIcon
              name={attachment.name}
              mime={attachment.mime}
              className={compact ? 'h-4 w-4 flex-shrink-0' : 'h-5 w-5 flex-shrink-0'}
            />

            <div className="flex-1 min-w-0">
              {onOpen ? (
                <button
                  type="button"
                  onClick={() => onOpen(attachment)}
                  title={name}
                  className={clsx(
                    'block w-full text-start truncate text-fg-body hover:text-champagne',
                    'transition-colors duration-200 ease-slide',
                    compact ? 'text-xs' : 'text-sm'
                  )}
                >
                  {name}
                </button>
              ) : (
                <p className={clsx('truncate text-fg-body', compact ? 'text-xs' : 'text-sm')} title={name}>
                  {name}
                </p>
              )}
              {/* In columns mode this is the NARROW-WINDOW fallback only:
                  below `lg` the columns are hidden and the same three facts
                  stack under the name, as they always did. */}
              <div
                className={clsx(
                  'flex items-center flex-wrap gap-x-2 text-fg-muted',
                  compact ? 'text-[11px] mt-0.5' : 'text-xs mt-1',
                  columns && 'lg:hidden'
                )}
              >
                {size && <span>{size}</span>}
                {attachment.uploadedBy && (
                  <>
                    {size && <span aria-hidden="true">·</span>}
                    <span className="truncate">{attachment.uploadedBy}</span>
                  </>
                )}
                {when && (
                  <>
                    {(size || attachment.uploadedBy) && <span aria-hidden="true">·</span>}
                    <time dateTime={attachment.uploadedAt} title={whenTitle}>
                      {when}
                    </time>
                  </>
                )}
              </div>
            </div>

            {/* ── The columns, on one line, at lg and up ── */}
            {columns && (
              <>
                <span className={COL_UPLOADER} title={attachment.uploadedBy || undefined}>
                  {attachment.uploadedBy || '—'}
                </span>
                <span className={COL_WHEN}>
                  {attachment.uploadedAt ? (
                    <time dateTime={attachment.uploadedAt} title={when}>
                      {whenTitle}
                    </time>
                  ) : (
                    '—'
                  )}
                </span>
                <span className={COL_SIZE}>{size || '—'}</span>
              </>
            )}

            {/* The type badge repeats what the leading file icon already says
                and is the one variable-width cell - dropped in columns mode so
                the action cluster stays aligned down the list. */}
            {!compact && !columns && (
              <Badge color="gray" className="text-[10px] flex-shrink-0">
                {fileTypeLabel(attachment.name, attachment.mime)}
              </Badge>
            )}

            {onDownload && (
              <button
                type="button"
                aria-label={t('Download')}
                title={t('Download')}
                disabled={busy}
                onClick={() => onDownload(attachment)}
                className="p-1.5 rounded-md text-fg-muted hover:text-champagne transition-colors duration-200 ease-slide disabled:opacity-50 flex-shrink-0"
              >
                <PiDownloadSimpleLight className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
              </button>
            )}

            {rowActions?.(attachment)}

            {removable(attachment) && (
              <button
                type="button"
                aria-label={t('Remove')}
                title={t('Remove')}
                disabled={busy}
                onClick={() => onRemove?.(attachment)}
                className="p-1.5 rounded-md text-fg-muted hover:text-danger-600 transition-colors duration-200 ease-slide disabled:opacity-50 flex-shrink-0"
              >
                <PiTrashLight className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );

  if (!columns) return rows;

  /* The header carries the same widths as the rows and a trailing spacer the
     size of the action cluster (two 28px icon buttons and their gap), so the
     labels sit over the cells they name. It is `lg`-only, like the columns. */
  return (
    <div className={clsx('flex-1 min-h-0 flex flex-col', className)} data-tour-id={dataTourId}>
      <div className="hidden lg:flex items-center gap-3 px-3 pb-1.5 flex-shrink-0">
        {onToggleSelect && <span className="w-4 flex-shrink-0" aria-hidden="true" />}
        <span className="w-5 flex-shrink-0" aria-hidden="true" />
        <span className="flex-1 min-w-0 text-[11px] font-medium text-fg-muted">{t('Name')}</span>
        <span className="w-40 flex-shrink-0 text-[11px] font-medium text-fg-muted">
          {t('Uploaded by')}
        </span>
        <span className="w-40 flex-shrink-0 text-[11px] font-medium text-fg-muted">
          {t('Uploaded')}
        </span>
        <span className="w-20 flex-shrink-0 text-end text-[11px] font-medium text-fg-muted">
          {t('Size')}
        </span>
        <span
          className="flex-shrink-0"
          style={{ width: (onDownload ? 28 : 0) + (onRemove ? 28 : 0) + (onDownload && onRemove ? 12 : 0) }}
          aria-hidden="true"
        />
      </div>
      {rows}
    </div>
  );
};

export default AttachmentList;
